import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  LayersControl,
  MapContainer,
  TileLayer,
  useMapEvents,
} from "react-leaflet";
import { Map as LeafletMap } from "leaflet";
import { BikeMarker } from "./BikeMarker";
import { HeatmapLayer } from "./HeatmapLayer";
import { GeofenceLayer } from "./GeofenceLayer";
import { useMapStore, type MapState } from "../../store/useMapStore";
import { useBikeStore } from "../../store/useBikeStore";
import { useVehicleCategoryStore } from "../../store/useVehicleCategoryStore";
import { MAP_CONFIG, UNCATEGORIZED_FILTER_VALUE } from "../../utils/constants";
import type { MapBounds } from "../../types/map";
import "leaflet/dist/leaflet.css";

// Fix for default markers in react-leaflet
import L from "leaflet";
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";
import { LoadingSpinner } from "../common/LoadingSpinner";
import { Check, ChevronDown } from "lucide-react";

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

L.Marker.prototype.options.icon = DefaultIcon;

interface MapEventsProps {
  onBoundsChange: (bounds: MapBounds) => void;
  onZoomChange: (zoom: number) => void;
}

const MapEvents: React.FC<MapEventsProps> = ({
  onBoundsChange,
  onZoomChange,
}) => {
  const map = useMapEvents({
    moveend: () => {
      const bounds = map.getBounds();
      onBoundsChange({
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest(),
      });
    },
    zoomend: () => {
      onZoomChange(map.getZoom());
    },
  });

  return null;
};

export const BikeMap: React.FC = () => {
  const mapRef = useRef<LeafletMap | null>(null);

  const {
    showHeatmap,
    showGeofences,
    showMovingOnly,
    center,
    zoom,
    statusFilter,
    categoryFilters,
    fetchHeatmapData,
    // fetchGeofences,
    setBounds,
    setZoom,
  } = useMapStore();
  const { bikes, selectBike, loadIfNeeded } = useBikeStore();

  const bikeMarkers = useMemo(
    () =>
      bikes
        .filter((bike) => bike.last_location)
        .map((bike) => ({
          bike_id: bike.bike_id,
          latitude: bike.last_location!.latitude,
          longitude: bike.last_location!.longitude,
          status: bike.status,
          category_id: bike.category_id ?? null,
          last_seen: bike.last_location!.timestamp,
          speed: bike.last_location?.speed,
          battery_level: bike.last_location?.battery_level,
          is_moving: (bike.last_location?.speed ?? 0) > 0.5,
        })),
    [bikes],
  );

  const filteredMarkers = useMemo(() => {
    const includeUncategorized = categoryFilters.includes(
      UNCATEGORIZED_FILTER_VALUE,
    );
    const selectedCategoryIds = new Set(
      categoryFilters.filter(
        (categoryId) => categoryId !== UNCATEGORIZED_FILTER_VALUE,
      ),
    );

    return bikeMarkers.filter((item) => {
      if (showMovingOnly && !item.is_moving) {
        return false;
      }
      if (statusFilter && item.status !== statusFilter) {
        return false;
      }

      if (categoryFilters.length === 0) {
        return false;
      }

      const isUncategorized = !item.category_id;
      const isSelectedCategory =
        !!item.category_id && selectedCategoryIds.has(item.category_id);

      if (!(isSelectedCategory || (includeUncategorized && isUncategorized))) {
        return false;
      }
      return true;
    });
  }, [showMovingOnly, statusFilter, categoryFilters, bikeMarkers]);

  const { categories } = useVehicleCategoryStore();

  const categoryMarkerMap = useMemo(() => {
    return new Map(
      categories.map((category) => [
        category.category_id,
        category.marker_image,
      ]),
    );
  }, [categories]);

  const categoryColorMap = useMemo(() => {
    return new Map(
      categories.map((category) => [category.category_id, category.color]),
    );
  }, [categories]);

  useEffect(() => {
    loadIfNeeded().catch(console.error);
    // fetchGeofences();
  }, [loadIfNeeded]);

  useEffect(() => {
    // Fetch heatmap data when enabled
    if (showHeatmap && mapRef.current) {
      const bounds = mapRef.current.getBounds();
      fetchHeatmapData({
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest(),
      });
    }
  }, [showHeatmap, fetchHeatmapData]);

  const handleBoundsChange = useMemo(() => {
    let timeoutId: NodeJS.Timeout;

    return (bounds: MapBounds) => {
      setBounds(bounds);

      // Clear previous timeout
      clearTimeout(timeoutId);

      // Debounce API calls to prevent excessive requests
      timeoutId = setTimeout(() => {
        if (showHeatmap) {
          fetchHeatmapData(bounds);
        }
      }, 300); // 300ms delay
    };
  }, [showHeatmap, fetchHeatmapData, setBounds]);

  const handleZoomChange = (newZoom: number) => {
    setZoom(newZoom);
  };

  const handleBikeClick = (bikeId: string) => {
    selectBike(bikeId);
    // You can add more logic here, like opening a details panel
  };

  return (
    <div className="h-full w-full relative">
      <MapContainer
        center={center}
        zoom={zoom}
        zoomDelta={MAP_CONFIG.ZOOM_DELTA}
        zoomSnap={MAP_CONFIG.ZOOM_SNAP}
        maxZoom={MAP_CONFIG.MAX_ZOOM}
        wheelPxPerZoomLevel={MAP_CONFIG.WHEEL_PX_PER_ZOOM_LEVEL}
        style={{ height: "100%", width: "100%" }}
        ref={mapRef}
        className="z-0"
      >
        <LayersControl position="bottomright">
          <LayersControl.BaseLayer checked name="Street">
            <TileLayer
              url={MAP_CONFIG.TILE_URL}
              attribution={MAP_CONFIG.TILE_ATTRIBUTION}
              maxZoom={MAP_CONFIG.MAX_ZOOM}
            />
          </LayersControl.BaseLayer>

          <LayersControl.BaseLayer name="Satellite">
            <TileLayer
              url={MAP_CONFIG.SATELLITE_TILE_URL}
              attribution={MAP_CONFIG.SATELLITE_TILE_ATTRIBUTION}
              maxZoom={MAP_CONFIG.MAX_ZOOM}
            />
          </LayersControl.BaseLayer>
        </LayersControl>

        <MapEvents
          onBoundsChange={handleBoundsChange}
          onZoomChange={handleZoomChange}
        />

        {/* Bike Markers */}
        {filteredMarkers.map((bike) => (
          <BikeMarker
            key={bike.bike_id}
            bike={bike}
            markerImageUrl={
              bike.category_id
                ? (categoryMarkerMap.get(bike.category_id)?.trim() ?? null)
                : null
            }
            makerColor={
              bike.category_id
                ? (categoryColorMap.get(bike.category_id)?.trim() ?? null)
                : null
            }
            onClick={handleBikeClick}
          />
        ))}

        {/* Heatmap Layer */}
        {showHeatmap && <HeatmapLayer />}

        {/* Geofence Layer */}
        {showGeofences && <GeofenceLayer />}
      </MapContainer>

      {/* Map Controls Overlay */}
      <div className="absolute top-4 right-4 z-10 space-y-2">
        <MapControls />
      </div>

      {/* Map Stats Overlay */}
      <div className="absolute bottom-4 left-4 z-10">
        <MapStats />
      </div>
    </div>
  );
};
// Map Controls Component
const MapControls: React.FC = () => {
  const {
    showHeatmap,
    showGeofences,
    statusFilter,
    showMovingOnly,
    categoryFilters,
    setShowHeatmap,
    setShowGeofences,
    setStatusFilter,
    setShowMovingOnly,
    setCategoryFilters,
  } = useMapStore();
  const { categories, loadIfNeeded: loadCategoriesIfNeeded } =
    useVehicleCategoryStore();
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const hasInitializedCategorySelection = useRef(false);

  useEffect(() => {
    loadCategoriesIfNeeded().catch(console.error);
  }, [loadCategoriesIfNeeded]);

  const allCategoryValues = useMemo(
    () => [
      UNCATEGORIZED_FILTER_VALUE,
      ...categories.map((category) => category.category_id),
    ],
    [categories],
  );
  const areAllCategoriesSelected =
    allCategoryValues.length > 0 &&
    allCategoryValues.every((value) => categoryFilters.includes(value));
  const selectedCategoryCount = categoryFilters.length;
  const hasUncategorizedSelected = categoryFilters.includes(
    UNCATEGORIZED_FILTER_VALUE,
  );

  useEffect(() => {
    if (hasInitializedCategorySelection.current || categories.length === 0) {
      return;
    }

    if (categoryFilters.length === 0) {
      setCategoryFilters(allCategoryValues);
    }

    hasInitializedCategorySelection.current = true;
  }, [
    categories.length,
    categoryFilters.length,
    setCategoryFilters,
    allCategoryValues,
  ]);

  const selectedCategoryLabel = areAllCategoriesSelected
    ? "All Categories"
    : selectedCategoryCount === 0
      ? "No Categories"
      : `${selectedCategoryCount} selected`;

  const toggleCategory = (categoryId: string) => {
    if (categoryFilters.includes(categoryId)) {
      setCategoryFilters(
        categoryFilters.filter((selectedId) => selectedId !== categoryId),
      );
      return;
    }
    setCategoryFilters([...categoryFilters, categoryId]);
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-3 space-y-2 min-w-48">
      <h4 className="font-medium text-gray-900 text-sm">Map Controls</h4>
      <div className="space-y-2 hidden">
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={showHeatmap}
            onChange={(e) => setShowHeatmap(e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm text-gray-700">Show Heatmap</span>
        </label>

        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={showGeofences}
            onChange={(e) => setShowGeofences(e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm text-gray-700">Show Geofences</span>
        </label>

        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={showMovingOnly}
            onChange={(e) => setShowMovingOnly(e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm text-gray-700">Moving Only</span>
        </label>
      </div>

      <div className="pt-2 border-t border-gray-200 hidden">
        <label className="block text-sm text-gray-700 mb-1">
          Status Filter
        </label>
        <select
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(e.target.value as MapState["statusFilter"])
          }
          className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="">All Bikes</option>
          <option value="active">Active Only</option>
          <option value="inactive">Inactive Only</option>
          <option value="maintenance">Maintenance Only</option>
        </select>
      </div>

      <div className="pt-2 border-t border-gray-200">
        <label className="block text-sm text-gray-700 mb-1">
          Category Filter
        </label>
        <button
          type="button"
          onClick={() => setIsCategoryOpen((open) => !open)}
          className="w-full text-left text-sm border border-gray-300 rounded px-2 py-1 flex items-center justify-between hover:bg-gray-50 focus:ring-primary-500 focus:border-primary-500"
        >
          <span>{selectedCategoryLabel}</span>
          <ChevronDown className="w-4 h-4 text-gray-500" />
        </button>

        {isCategoryOpen && (
          <>
            <div
              className="fixed inset-0 z-[9998]"
              onClick={() => setIsCategoryOpen(false)}
            />
            <div className="absolute right-0 mt-2 w-64 max-h-72 overflow-y-auto bg-white rounded-lg shadow-lg border border-gray-200 z-[9999]">
              <div className="p-2 border-b border-gray-100">
                <button
                  type="button"
                  onClick={() =>
                    setCategoryFilters(
                      areAllCategoriesSelected ? [] : allCategoryValues,
                    )
                  }
                  className="flex items-center gap-2 w-full text-left px-2 py-1.5 text-sm rounded hover:bg-blue-50"
                >
                  <div
                    className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                      areAllCategoriesSelected
                        ? "bg-blue-600 border-blue-600"
                        : "border-gray-300"
                    }`}
                  >
                    {areAllCategoriesSelected && (
                      <Check className="w-3 h-3 text-white" />
                    )}
                  </div>
                  All Categories
                </button>
              </div>

              <div className="p-2 space-y-1">
                <button
                  type="button"
                  onClick={() => toggleCategory(UNCATEGORIZED_FILTER_VALUE)}
                  className="flex items-center gap-2 w-full text-left px-2 py-1.5 text-sm rounded hover:bg-blue-50"
                >
                  <div
                    className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                      hasUncategorizedSelected
                        ? "bg-blue-600 border-blue-600"
                        : "border-gray-300"
                    }`}
                  >
                    {hasUncategorizedSelected && (
                      <Check className="w-3 h-3 text-white" />
                    )}
                  </div>
                  Uncategorized
                </button>

                {categories.map((category) => {
                  const isChecked = categoryFilters.includes(
                    category.category_id,
                  );
                  return (
                    <button
                      key={category.category_id}
                      type="button"
                      onClick={() => toggleCategory(category.category_id)}
                      className="flex items-center gap-2 w-full text-left px-2 py-1.5 text-sm rounded hover:bg-blue-50"
                    >
                      <div
                        className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                          isChecked
                            ? "bg-blue-600 border-blue-600"
                            : "border-gray-300"
                        }`}
                      >
                        {isChecked && <Check className="w-3 h-3 text-white" />}
                      </div>
                      {category.category_name}
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
// Map Stats Component
const MapStats: React.FC = () => {
  const { bikes, loading: loadingBikes } = useBikeStore();
  const bikeMarkers = bikes.filter((bike) => bike.last_location);
  const stats = {
    total: bikeMarkers.length,
    active: bikeMarkers.filter((b) => b.status === "active").length,
    moving: bikeMarkers.filter((b) => (b.last_location?.speed ?? 0) > 0.5)
      .length,
    lowBattery: bikeMarkers.filter(
      (b) =>
        typeof b.last_location?.battery_level === "number" &&
        b.last_location.battery_level < 20,
    ).length,
  };
  if (loadingBikes) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-3">
        <div className="flex items-center space-x-2">
          <LoadingSpinner size="sm" />
          <span className="text-sm text-gray-600">Loading bikes...</span>
        </div>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-lg shadow-lg p-3 space-y-1">
      <h4 className="font-medium text-gray-900 text-sm">Map Stats</h4>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        <div className="text-gray-600">
          Total:{" "}
          <span className="font-medium text-gray-900">{stats.total}</span>
        </div>
        <div className="text-gray-600">
          Active:{" "}
          <span className="font-medium text-green-600">{stats.active}</span>
        </div>
        <div className="text-gray-600">
          Moving:{" "}
          <span className="font-medium text-blue-600">{stats.moving}</span>
        </div>
        <div className="text-gray-600">
          Low Battery:{" "}
          <span className="font-medium text-red-600">{stats.lowBattery}</span>
        </div>
      </div>
    </div>
  );
};
